/**
 * WHAT A RECORD REFERENCE IS ALLOWED TO SAY (#2325 §3, on top of #1705).
 *
 * A separate module from `queries.ts` for `recentsEmptyState.ts`'s reason: the
 * decision is a row, two booleans and a noun, it decides what a person reads about a
 * record they may or may not be allowed to know exists, and it is worth being
 * able to state every case without mounting a query client.
 *
 * ── the four answers, and why there are four ───────────────────────────────
 * `core.titles_of` (#1472 / ADR F-33) is SECURITY INVOKER and returns NOTHING
 * for a record the caller cannot see, for an id that does not exist, and for an
 * entity with no title declaration. Those three are indistinguishable ON
 * PURPOSE — that is what makes non-disclosure indistinguishable from absence.
 *
 * Only a RETURNED ROW proves the caller may read the record. So:
 *
 *   the read FAILED       → UNAVAILABLE   wins over any row a previous read
 *                                         left behind (see below)
 *   row with a title      → NAMED         the governed display name
 *   row with a NULL title → UNNAMED       authorized; the expression produced
 *                                         nothing (an organization saved with
 *                                         neither a legal nor a display name)
 *   still reading         → LOADING
 *   no row, but the CALLER read the row itself through a governed door
 *                         → UNNAMED       a list's own page rows (`readable`)
 *   no row, otherwise     → UNAVAILABLE   deleted, refused, never existed — or
 *                                         an entity that declares no title,
 *                                         which the door cannot answer for
 *
 * The last line changed (#2757 R2, ROOT #2425 c.118656 §1). It used to read an
 * absent row of an entity WITHOUT a title declaration as a structural `Unnamed
 * {type}`, on the argument that every record of that entity looks alike. But
 * that sentence claims the record is there, and the absence of a declaration is
 * not evidence that the caller may read it: a hidden or deleted target read as
 * a live `Unnamed vat rate` beside a link. `title_declared` is therefore no
 * longer an input. An entity whose records deserve a name gets a title
 * declaration in its own module, never a guess here.
 *
 * ── what is NEVER an answer ────────────────────────────────────────────────
 * The uuid. Not as a label, not as a fallback, not as a title attribute. That
 * is #1705's ruling and this module is where it is enforceable: there is no
 * branch here that can return an id, because an id is not one of its inputs.
 *
 * ── a failed READ is not a fact about the record ───────────────────────────
 * When the door did not answer, every ref is UNAVAILABLE. That includes a ref a
 * PREVIOUS read named: a query client keeps the last successful data when a
 * refetch fails, and showing that former title or link during an outage, or
 * after access was withdrawn, would assert a record on the strength of an answer
 * that is no longer current (#2757 R2, ROOT #2425 c.118656 §2). The next
 * successful answer restores it. A background refetch that has NOT failed keeps
 * its data. The surface above has an honest sentence for an outage
 * (`recentsEmptyState`).
 */

export type RecordNamingState = 'loading' | 'named' | 'unnamed' | 'unavailable'

export interface RecordNaming {
  state: RecordNamingState
  /** What a person reads. Never an id, in any state. */
  title: string
}

export interface RecordNamingInput {
  /** The row `core.titles_of` returned for this ref, or `undefined` for none. */
  row: { title: string | null } | undefined
  /** The entity's governed singular label, already localized by the registry. */
  typeLabel: string
  /** The batch read is still in flight. */
  pending: boolean
  /**
   * The batch read failed — including a REFETCH that failed after an earlier
   * success, while the query client still holds that earlier data. Distinct
   * from "answered with nothing".
   */
  failed?: boolean
  /**
   * The CALLER holds this row from its own governed read — a list page it has
   * just read under the caller's RLS. That is the "existing governed answer
   * that establishes a readable row" (ROOT #2425 c.118656 §1), so a missing
   * titles_of row (an entity with no title declaration) is `Unnamed {type}`,
   * not unavailable. Never set from the entity's type or registration.
   */
  readable?: boolean
}

export function recordNaming(
  { row, typeLabel, pending, failed = false, readable = false }: RecordNamingInput,
): RecordNaming {
  /* First, before any row: a row retained from a previous answer is not an
     answer to the read that just failed (#2757 R2, ROOT #2425 c.118656 §2). */
  if (failed) return { state: 'unavailable', title: 'Record unavailable' }
  if (row?.title) return { state: 'named', title: row.title }
  /* A row came back and its title expression yielded nothing. The caller may
     read this record — the door proved that by returning it — so this is a fact
     about the record, not a failure. */
  if (row) return { state: 'unnamed', title: unnamedTitle(typeLabel) }
  if (pending) return { state: 'loading', title: 'Loading record…' }
  /* No titles_of row, but the caller read the row itself through a governed
     door: readable, and the door had no name to give (§1's exception). */
  if (readable) return { state: 'unnamed', title: unnamedTitle(typeLabel) }
  /* No row: deleted, refused, never existed, or an entity with no title
     declaration. Nothing here proves the caller may read it, so it is never
     `Unnamed {type}` (#2757 R2, ROOT #2425 c.118656 §1). */
  return { state: 'unavailable', title: 'Record unavailable' }
}

/**
 * `Unnamed organization`, not `Unnamed Organization` and never `Organization
 * 3f2a…`. The noun is the registry's own localized singular, lower-cased by the
 * locale rather than by an ASCII assumption — Turkish `I` is the standing
 * counter-example, and the label is data a deployment can set.
 */
function unnamedTitle(typeLabel: string): string {
  return unnamedRecordTitle(typeLabel)
}

/**
 * The same sentence, for a caller that does not hold the registry's noun.
 *
 * A governed search door answers rows, not identities: it can return a row
 * whose text columns are all empty, and the caller has the row and the entity
 * but not always that entity's singular label. `Unnamed record` is the
 * truthful degradation of `Unnamed {type}` — it drops the noun rather than
 * inventing one, and it is still never the uuid, which is the half of #1705
 * that matters. ONE definition, because two copies of a ruling's wording is how
 * one of them stops following it (rule 42).
 */
export function unnamedRecordTitle(typeLabel?: string | null): string {
  const noun = typeLabel?.trim()
  return noun ? `Unnamed ${noun.toLocaleLowerCase()}` : 'Unnamed record'
}

/** A ref the surface may draw a row for. The inverse is a stale or refused item. */
export function isListable(state: RecordNamingState): boolean {
  return state !== 'unavailable'
}
